import { type OrgKey, OrgKeyStatus } from '@compass/client/types/org_key';

const ORG_ADMIN_SLUG = 'organization-admin';

type RoleLike = { slug?: string; organization_id?: string };

/**
 * Whether the current user may manage org keys for the selected org: a system admin
 * may for any org, anyone else only where they hold the organization-admin role. UI
 * gate only — the server enforces the real authorization check on every request.
 */
export function canManageOrgKeys(
  roles: RoleLike[],
  isSystemAdmin: boolean,
  organizationId: string | null,
): boolean {
  if (!organizationId) return false;
  if (isSystemAdmin) return true;

  return roles.some(
    role => role.slug === ORG_ADMIN_SLUG && role.organization_id === organizationId,
  );
}

/**
 * Compass serializes proto enums as numbers on the wire, so `status` normally arrives
 * as the numeric `OrgKeyStatus`. The string enum name is accepted defensively in case a
 * caller passes the symbolic form directly (e.g. from a test fixture).
 */
export function orgKeyStatusLabel(key: Pick<OrgKey, 'status'>): 'Active' | 'Expired' {
  const status = key.status as unknown;
  if (status === OrgKeyStatus.ORG_KEY_STATUS_EXPIRED || status === 'ORG_KEY_STATUS_EXPIRED') {
    return 'Expired';
  }
  return 'Active';
}
